import React, { memo } from 'react';
import { Box, Chip, Stack, useTheme, alpha, Typography, Tooltip } from '@mui/material';
import { CallSplit } from '@mui/icons-material';

interface Props {
  /** The OR-divider text — usually "OR"; can be a translated string. */
  label: string;
  /** Number of alternatives — surfaced in the header for context. */
  memberCount: number;
  /** The rendered SectionQuestionCards for each alternative. */
  children: React.ReactNode[];
  /**
   * Emitted so the caller can render dividers between arbitrary elements
   * without knowing about member internals. Alternative to interleaving:
   * pass one flat children array here.
   */
}

/**
 * Wraps a set of choice-paired questions with a warning-toned accent border
 * and interleaves an "OR" chip between the members.
 *
 * Rendering pattern: `children` is expected to be an array — one React node
 * per group member. We insert the OR divider between adjacent members.
 */
const ChoiceGroupBlock = memo<Props>(({ label, memberCount, children }) => {
  const theme = useTheme();
  const nodes = React.Children.toArray(children);

  return (
    <Box
      sx={{
        mb: 2,
        p: 1.5,
        borderRadius: 2,
        border: `1px solid ${alpha(theme.palette.warning.main, 0.4)}`,
        bgcolor: alpha(theme.palette.warning.main, 0.03),
      }}
    >
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
        <CallSplit sx={{ fontSize: 16, color: 'warning.dark' }} />
        <Typography variant="caption" sx={{ color: 'warning.dark', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5 }}>
          Choice group — student picks one
        </Typography>
        <Tooltip title="Marks must be identical across all alternatives">
          <Chip
            label={`${memberCount} alternatives`}
            size="small"
            sx={{
              height: 20,
              fontSize: '0.7rem',
              bgcolor: alpha(theme.palette.warning.main, 0.1),
              color: 'warning.dark',
            }}
          />
        </Tooltip>
      </Stack>

      <Box>
        {nodes.map((node, idx) => (
          <React.Fragment key={idx}>
            {node}
            {idx < nodes.length - 1 && (
              <Box sx={{ display: 'flex', justifyContent: 'center', my: 1 }}>
                <Chip
                  label={label}
                  size="small"
                  sx={{
                    height: 22,
                    px: 0.5,
                    fontWeight: 700,
                    letterSpacing: 0.8,
                    bgcolor: 'background.paper',
                    border: `1px solid ${alpha(theme.palette.warning.main, 0.5)}`,
                    color: 'warning.dark',
                  }}
                />
              </Box>
            )}
          </React.Fragment>
        ))}
      </Box>
    </Box>
  );
});

ChoiceGroupBlock.displayName = 'ChoiceGroupBlock';

export default ChoiceGroupBlock;
